import { Flex, Space, theme, Typography, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';
import { MetricSplit } from '@/types/content';
import { formatNumber } from '@/utils';

const MetricPercentage = ({ value, total }: { value: number; total: number }) => {
  if (value <= 0) {
    return <Typography.Text>-</Typography.Text>;
  }

  const percentage = total === 0 ? 'n/a' : `${((value / total) * 100).toFixed(0)}%`;
  return (
    <Space size={8}>
      <Typography.Text>{formatNumber('en', value)}</Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {percentage}
      </Typography.Text>
    </Space>
  );
};

type MetricEntity = 'episode' | 'show' | 'network';

const VideoViewsColumnTitle = ({
  entity,
  labelSuffix = '',
}: {
  entity: MetricEntity;
  labelSuffix?: string;
}) => {
  const { token } = theme.useToken();

  return (
    <Flex align="center" gap={token.marginXXS} justify="flex-end">
      {`Video views${labelSuffix}`}
      <Tooltip
        placement="bottom"
        title={
          <Flex vertical gap={token.marginXXS}>
            <Typography.Text strong style={{ color: token.colorTextLightSolid }}>
              Video podcast consumption
            </Typography.Text>
            <Typography.Text style={{ color: token.colorTextLightSolid }}>
              Number of times the video version of this {entity} was viewed during the selected
              period.{' '}
              <em>
                Reflects Apple Podcasts only, with more comprehensive data to come in the future.
              </em>
            </Typography.Text>
            <Typography.Text style={{ color: token.colorTextLightSolid }}>
              The percentage shows video views as a share of total {entity} consumption (all
              downloads + views).
            </Typography.Text>
          </Flex>
        }
      >
        <InfoCircleOutlined
          style={{ fontSize: token.fontSize, color: token.colorTextDescription }}
          onClick={(event) => event.stopPropagation()}
          aria-label="Video views info"
        />
      </Tooltip>
    </Flex>
  );
};

/**
 * Metric columns for the Content Performance tables.
 * With `showVideoMetrics: false` (no HLS-enabled show selected) only "Audio downloads" is returned;
 * the parent table's flexible label column absorbs the freed width.
 */
export const getMetricColumns = <TRow extends MetricSplit>(
  entity: MetricEntity,
  {
    showVideoMetrics = true,
    sortable = true,
    labelSuffix = '',
  }: { showVideoMetrics?: boolean; sortable?: boolean; labelSuffix?: string } = {}
): Array<ColumnType<TRow>> => {
  const audioDownloadsColumn: ColumnType<TRow> = {
    title: `Audio downloads${labelSuffix}`,
    dataIndex: 'audioDownloads',
    sorter: sortable,
    align: 'right',
    width: 150,
    onHeaderCell: () => ({ style: { whiteSpace: 'normal' } }),
    render: (_: unknown, item: TRow) =>
      showVideoMetrics ? (
        <MetricPercentage value={item.audioDownloads} total={item.downloadsAndViews} />
      ) : (
        formatNumber('en', item.audioDownloads)
      ),
  };

  if (!showVideoMetrics) {
    return [audioDownloadsColumn];
  }

  return [
    {
      title: `Downloads & views${labelSuffix}`,
      sorter: sortable,
      dataIndex: 'downloadsAndViews',
      align: 'right',
      width: 160,
      onHeaderCell: () => ({ style: { whiteSpace: 'normal' } }),
      render: (value: number) => formatNumber('en', value),
    },
    audioDownloadsColumn,
    {
      title: <VideoViewsColumnTitle entity={entity} labelSuffix={labelSuffix} />,
      dataIndex: 'videoViews',
      sorter: sortable,
      align: 'right',
      width: 130,
      onHeaderCell: () => ({ style: { whiteSpace: 'normal' } }),
      render: (_: unknown, item: TRow) => (
        <MetricPercentage value={item.videoViews} total={item.downloadsAndViews} />
      ),
    },
  ];
};
